import React, {useEffect, useState} from 'react'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import {
  addItem,
  clearCart,
  hideItemEditor,
  selectCartHasItems,
  selectCartHasMonthlyItems,
  selectCartItemCount,
  selectCartItemsProperty,
  selectIsItemEditorOpen,
  selectItemEditorMode,
  selectItemToEdit,
  updateItem,
  updateItemEditor
} from '../../store/slices/shoppingCartSlice'
import ShoppingCartServiceItem from './ShoppingCartServiceItem'
import * as types from '../../schema'
import {AppView} from '../../schema'
import ShoppingCartItem from './ShoppingCartItem'
import {setAppView} from '../../store/slices/appViewSlice'
import {addToastNotification} from '../../store/slices/toastNotificationSlice'
import {generateUUID} from '../../services/utils'
import {selectSearchParams} from '../../store/slices/searchParamsSlice'
import {useSearchServiceOffersQuery} from '../../services/ibeApi'
import {getLogger} from '../../services/logging'
import Alert from '../common/Alert'
import {FocusTrap} from 'focus-trap-react'
import {pushDataLayerEvent} from '../../services/dataLayer'
import {booking} from '@whyhotel/common-schema'
import {isMonthlyOffer} from '../../services/offerUtils'
import useCartItemRefresh from '../../hooks/useCartItemRefresh'

const logger = getLogger('ShoppingCartItemEditorModal')

const MAX_CART_ITEMS = 6

const ShoppingCartItemEditorModal = () => {
  const dispatch = useAppDispatch()
  const [isMonthlyTermsAccepted, setMonthlyTermsAccepted] = useState<boolean>(
    false
  )
  const isOpen = useAppSelector(selectIsItemEditorOpen)
  const item = useAppSelector(selectItemToEdit) as types.ShoppingCartItem
  const editorMode = useAppSelector(selectItemEditorMode)
  const search = useAppSelector(selectSearchParams)
  const {refreshCartItem} = useCartItemRefresh()
  const cartHasItems = useAppSelector(selectCartHasItems)
  const cartItemsPropertyId = useAppSelector(selectCartItemsProperty)
  const cartHasMonthlyItems = useAppSelector(selectCartHasMonthlyItems)
  const cartItemCount = useAppSelector(selectCartItemCount)
  const isMultiMonthlyViolation =
    editorMode === 'Add' &&
    (cartHasMonthlyItems || (isMonthlyOffer(item?.offer) && cartHasItems))
  const isMultiPropertyViolation =
    cartHasItems && cartItemsPropertyId !== item?.propertyId
  const isMaxCartItemsViolation =
    cartHasItems && cartItemCount >= MAX_CART_ITEMS
  const ratePlanId = item?.offer?.ratePlan?.id || ''
  const isMonthly = isMonthlyOffer(item?.offer)
  const serviceOffersQuery = useSearchServiceOffersQuery(
    {
      ratePlanId,
      guests: item?.guests || 1,
      dateRange: search.dateRange
    },
    {skip: !item || !ratePlanId || isMonthly}
  )
  const isLoading = serviceOffersQuery.isLoading || false
  const serviceOffers: booking.ServiceOffer[] =
    serviceOffersQuery.data?.services || []
  const additionalServiceIds = item?.additionalServices
    ? item.additionalServices.map(({service}) => service.id)
    : []

  useEffect(() => {
    setMonthlyTermsAccepted(false)
  }, [item])

  if (!item) {
    return null
  }

  return (
    <>
      {isOpen && (
        <FocusTrap active={isOpen}>
          <div
            className="modal modal-open"
            onClick={e => {
              if (e.target === e.currentTarget) {
                dispatch(hideItemEditor())
              }
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-box w-[95%] max-w-screen-md max-h-[95%] px-4 md:px-6">
              <h1 className="text-lg font-bold">
                {editorMode === 'Add' ? 'Add room' : 'Edit room details'}
              </h1>
              <button
                className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4"
                onClick={() => {
                  dispatch(hideItemEditor())
                }}
                aria-label="Close shopping cart item."
              >
                ✕
              </button>

              {isMultiPropertyViolation && (
                <Alert type="error" className="my-4">
                  <p className="text-left">
                    You cannot add rooms from multiple properties to the
                    shopping cart. If you would like to book rooms from multiple
                    properties, you will need to complete the booking process
                    for each property individually. You may also clear existing
                    items in the shopping cart to continue adding this room.
                  </p>
                  <button
                    className="btn mt-4"
                    onClick={() => {
                      logger.info({
                        message:
                          'Clearing shopping cart to add room violating multi property contraint...',
                        data: {item, cartItemsPropertyId}
                      })
                      pushDataLayerEvent({
                        event: 'Shopping_Cart_Clear',
                        category: 'IBE',
                        value: 1
                      })
                      dispatch(clearCart())
                    }}
                    aria-label="Clear shopping cart."
                  >
                    Clear shopping cart
                  </button>
                </Alert>
              )}

              {!isMultiPropertyViolation && isMaxCartItemsViolation && (
                <Alert type="error" className="my-4">
                  <p className="text-left">
                    The shopping cart can contain a maximum of {MAX_CART_ITEMS}{' '}
                    rooms. If you wish to add this room, you must remove an
                    existing room from the cart.
                  </p>
                </Alert>
              )}

              {!isMultiPropertyViolation &&
                !isMaxCartItemsViolation &&
                isMultiMonthlyViolation && (
                  <Alert type="error" className="my-4">
                    <p className="text-left">
                      Rooms with monthly rates must be booked one at a time. If
                      you would like to book multiple rooms with monthly rates,
                      you will need to complete the booking process for each
                      room individually. You may also clear existing items in
                      the shopping cart to continue adding this room.
                    </p>
                    <button
                      className="btn mt-4"
                      onClick={() => {
                        logger.info({
                          message:
                            'Clearing shopping cart to add room violating multi monthly stay contraint...',
                          data: {item, cartItemsPropertyId}
                        })
                        pushDataLayerEvent({
                          event: 'Shopping_Cart_Clear',
                          category: 'IBE',
                          value: 1
                        })
                        dispatch(clearCart())
                      }}
                      aria-label="Clear shopping cart."
                    >
                      Clear shopping cart
                    </button>
                  </Alert>
                )}

              {/* Additional Services */}

              {isLoading && (
                <div className="flex flex-col items-center justify-center h-48">
                  <span className="loading loading-spinner"></span>
                  <h2>Loading services...</h2>
                </div>
              )}

              {!isLoading && !isMonthly && serviceOffers.length > 0 && (
                <>
                  <h2 className="font-semibold text-md my-1">
                    Choose Additional Services:
                  </h2>
                  <div className="flex flex-col gap-2">
                    {serviceOffers.map(
                      (serviceOffer: booking.ServiceOffer, index: number) => {
                        return (
                          <ShoppingCartServiceItem
                            key={index}
                            serviceOffer={serviceOffer}
                            isSelected={additionalServiceIds.includes(
                              serviceOffer.service.id
                            )}
                            onSelectionChange={(isSelected: boolean) => {
                              logger.info({
                                message: 'Updated service selection...',
                                data: {isSelected, serviceOffer}
                              })

                              const additionalServices = isSelected
                                ? [...item.additionalServices, serviceOffer]
                                : item.additionalServices.filter(service => {
                                    return (
                                      service.service.id !==
                                      serviceOffer.service.id
                                    )
                                  })

                              const updatedItem = {
                                ...item,
                                additionalServices
                              }

                              dispatch(updateItemEditor(updatedItem))
                            }}
                          />
                        )
                      }
                    )}
                  </div>
                </>
              )}

              <div className="card card-compact border bg-white shadow w-full mt-4">
                <div className="card-body">
                  <ShoppingCartItem
                    item={item}
                    isCart={false}
                    showItemIndex={false}
                  />

                  {isMonthly && editorMode === 'Add' && (
                    <>
                      <div className="border-t my-2"></div>
                      <div className="flex flex-row gap-2 border-t-1">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-accent"
                          checked={isMonthlyTermsAccepted}
                          onChange={e => {
                            const isAccepted = e.currentTarget.checked
                            setMonthlyTermsAccepted(!isMonthlyTermsAccepted)

                            logger.info({
                              message: 'Toggle monthly terms acceptance',
                              data: {isAccepted}
                            })

                            pushDataLayerEvent({
                              event: 'Shopping_Cart_Accept_Monthly_Terms',
                              category: 'IBE',
                              value: 1
                            })
                          }}
                        />
                        <span className="text-sm">
                          I understand this reservation is not fully confirmed
                          until I complete guest verification, including a soft
                          credit check. Placemakr will email me a verification
                          link immediately after booking.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                <div className="modal-action">
                  {editorMode === 'Add' ? (
                    <>
                      <button
                        className="flex flex-col items-center gap-0 btn btn-ghost"
                        disabled={
                          isLoading ||
                          isMultiPropertyViolation ||
                          isMultiMonthlyViolation ||
                          isMaxCartItemsViolation ||
                          (isMonthly && !isMonthlyTermsAccepted)
                        }
                        onClick={() => {
                          logger.info({
                            message: 'Added room to cart.',
                            data: {item}
                          })
                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Add',
                            category: 'IBE',
                            value: 1
                          })
                          dispatch(addItem(item))
                          dispatch(hideItemEditor())
                          // show toast notification
                          dispatch(
                            addToastNotification({
                              id: generateUUID(),
                              type: 'add',
                              message:
                                'Room successfully added to your shopping cart.'
                            })
                          )

                          // recalculate final offer for monthly rates
                          if (isMonthly) {
                            logger.info({
                              message:
                                'Recalculating cart item pricing for monthly rate'
                            })
                            refreshCartItem(item)
                          }
                        }}
                        aria-label="Add room to shopping cart and continue shopping."
                      >
                        Add Room
                      </button>

                      <button
                        className="btn btn-secondary"
                        disabled={
                          isLoading ||
                          isMultiPropertyViolation ||
                          isMultiMonthlyViolation ||
                          isMaxCartItemsViolation ||
                          (isMonthly && !isMonthlyTermsAccepted)
                        }
                        onClick={() => {
                          logger.info({
                            message:
                              'Added room to cart, continuing to checkout.',
                            data: {item}
                          })

                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Add_Checkout',
                            category: 'IBE',
                            value: 1
                          })

                          dispatch(addItem(item))
                          dispatch(hideItemEditor())
                          dispatch(setAppView(AppView.CHECKOUT))

                          // recalculate final offer for monthly rates
                          if (isMonthly) {
                            logger.info({
                              message:
                                'Recalculating cart item pricing for monthly rate'
                            })
                            refreshCartItem(item)
                          }
                        }}
                        aria-label="Add room to shopping cart and checkout."
                      >
                        Add Room & Checkout
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          logger.info({
                            message: 'Cancelled edits to room item...',
                            data: {item}
                          })

                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Cancel_Edit',
                            category: 'IBE',
                            value: 1
                          })

                          dispatch(hideItemEditor())
                        }}
                        aria-label="Cancel edits to shopping cart item."
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={isLoading || isMultiPropertyViolation}
                        onClick={() => {
                          logger.info({
                            message: 'Saved edits to room item...',
                            data: {item}
                          })
                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Save_Edit',
                            category: 'IBE',
                            value: 1
                          })
                          dispatch(updateItem(item))
                          dispatch(hideItemEditor())
                        }}
                        aria-label="Save edits to shopping cart item."
                      >
                        Save
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
    </>
  )
}

export default ShoppingCartItemEditorModal
